import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Star, ArrowLeft, Check, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { tastingApi } from '@/api/services';
import { useTastingStore } from '@/stores/tastingStore';
import RatingInput from '@/components/shared/RatingInput';

export default function SummaryStep() {
  const navigate = useNavigate();
  const {
    session,
    getCurrentSpirit,
    currentSpiritIndex,
    spirits,
    updateSpirit,
    previousStep,
    nextSpirit,
    isLastSpirit,
    setCurrentStep,
  } = useTastingStore();

  const spirit = getCurrentSpirit();
  const [overallRating, setOverallRating] = useState(spirit?.overall_rating || 0);
  const [personalNotes, setPersonalNotes] = useState(spirit?.personal_notes || '');

  // Complete session mutation
  const completeMutation = useMutation({
    mutationFn: () => tastingApi.completeSession(session!.uuid),
    onSuccess: () => {
      toast.success('Tasting abgeschlossen!');
      navigate(`/completed/${session!.uuid}`);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSave = () => {
    // Save current spirit
    updateSpirit(currentSpiritIndex, {
      overall_rating: overallRating,
      personal_notes: personalNotes || undefined,
    });

    if (isLastSpirit()) {
      // Complete the session
      completeMutation.mutate();
    } else {
      // Move to next spirit
      nextSpirit();
      setCurrentStep('aroma');
    }
  };

  if (!spirit) {
    return (
      <div className="text-center py-12">
        <p className="text-dark-400">Keine Spirituose ausgewählt</p>
      </div>
    );
  }

  // Calculate component ratings
  const componentRatings = [
    { label: 'Aroma', value: spirit.aroma_rating },
    { label: 'Geschmack', value: spirit.taste_rating },
    { label: 'Abgang', value: spirit.finish_rating },
  ];

  const averageRating = componentRatings.filter(r => r.value).length > 0
    ? componentRatings.reduce((sum, r) => sum + (r.value || 0), 0) / componentRatings.filter(r => r.value).length
    : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="badge-primary mb-4">
          Spirituose {currentSpiritIndex + 1} von {spirits.length}
        </div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-dark-50 mb-2">
          {spirit.spirit_name}
        </h1>
        {spirit.spirit_brand && (
          <p className="text-dark-400">{spirit.spirit_brand}</p>
        )}
      </div>

      {/* Step Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Star className="w-8 h-8 text-primary-500" />
        </div>
        <h2 className="text-xl font-semibold text-dark-100 mb-2">Gesamtbewertung</h2>
        <p className="text-dark-400 text-sm max-w-md mx-auto">
          Wie bewertest du diese Spirituose insgesamt?
        </p>
      </motion.div>

      {/* Component Ratings Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        <h3 className="text-lg font-semibold text-dark-100 mb-4">Deine Bewertungen</h3>
        <div className="grid grid-cols-3 gap-4 mb-4">
          {componentRatings.map((rating) => (
            <div key={rating.label} className="text-center p-3 bg-dark-800/50 rounded-xl">
              <div className="text-2xl font-bold text-dark-50">{rating.value || '-'}</div>
              <div className="text-sm text-dark-400">{rating.label}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 pt-4 border-t border-dark-700">
          <span className="text-dark-400">Durchschnitt:</span>
          <span className="text-xl font-bold text-primary-400">{averageRating.toFixed(1)}</span>
        </div>
      </motion.div>

      {/* Descriptors Summary */}
      {(spirit.aroma_descriptors?.length || spirit.taste_descriptors?.length || spirit.finish_descriptors?.length) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card"
        >
          <h3 className="text-lg font-semibold text-dark-100 mb-4">Ausgewählte Aromen</h3>
          <div className="flex flex-wrap gap-2">
            {[
              ...(spirit.aroma_descriptors || []),
              ...(spirit.taste_descriptors || []),
              ...(spirit.finish_descriptors || []),
            ].map((descriptor, i) => (
              <span key={i} className="badge-primary">{descriptor}</span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Botanicals Summary */}
      {spirit.detected_botanicals && spirit.detected_botanicals.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h3 className="text-lg font-semibold text-dark-100 mb-4">Erkannte Botanicals</h3>
          <div className="flex flex-wrap gap-2">
            {spirit.detected_botanicals.map((botanical, i) => (
              <span key={i} className="badge bg-success-500/20 text-success-400">{botanical}</span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Overall Rating */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="card"
      >
        <h3 className="text-lg font-semibold text-dark-100 mb-4">Gesamtbewertung</h3>
        <RatingInput value={overallRating} onChange={setOverallRating} />
      </motion.div>

      {/* Personal Notes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        <h3 className="text-lg font-semibold text-dark-100 mb-4">Persönliche Notizen (optional)</h3>
        <textarea
          value={personalNotes}
          onChange={(e) => setPersonalNotes(e.target.value)}
          placeholder="Deine persönlichen Gedanken zu dieser Spirituose..."
          className="input min-h-[100px] resize-none"
        />
      </motion.div>

      {/* Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="flex gap-3"
      >
        <button
          onClick={previousStep}
          className="btn-secondary flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>
        <button
          onClick={handleSave}
          disabled={overallRating === 0 || completeMutation.isPending}
          className="btn-primary flex-1 flex items-center justify-center gap-2"
        >
          {completeMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Wird gespeichert...
            </>
          ) : isLastSpirit() ? (
            <>
              <Check className="w-5 h-5" />
              Tasting abschließen
            </>
          ) : (
            <>
              Nächste Spirituose
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
}
